import { Loader } from '../Widgets';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { RootState } from 'store/store';
import { useSelector } from 'react-redux';
import ArthaGateway from 'service/ArthaGateway';
import { Party, SaleOrder, Product, OrderItem, OrderDocument, ProductMaster, ProductStyle, OrderStatuses } from 'service';
import defaultImage from "../../assets/default.png"
import { faChevronLeft, faFile, faGripVertical, faTableCells, faTableList, faTrash, faPlus, faMinus, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import toast, { Toaster } from 'react-hot-toast';
import { FileUpload } from '../Widgets';
import { useParams } from 'react-router';

export const OrderDetails = () => {
    const user = useSelector((state: RootState) => state.appdata);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [party, setParty] = useState<Party>();
    const [order, setOrder] = useState<SaleOrder>(new SaleOrder());
    const [isCancelling, setIsCancelling] = useState<boolean>(false);
    const [cancelComment, setCancelComment] = useState<string>("");

    const params = useParams();
    const orderId = params.id ? parseInt(params.id) : 0;

    const navigate = useNavigate();

    useEffect(() => {
        if (user.profile) {
            setParty(user.profile.profile);
            setIsLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (orderId > 0) {
            loadOrderDetails();
        }
    }, [orderId]);

    const loadOrderDetails = () => {
        if (orderId <= 0) {
            return;
        }
        ArthaGateway.getSalesOrder(orderId).then((data) => {
            setOrder(data);
            setIsLoading(false);
        }).catch(error => {
            toast.error("Error loading order details");
            setIsLoading(false);
            console.log(error);
        });
    };

    const getDisplayImage = (item) => {
        if (item.customImageUrl && item.customImageUrl.length > 0) {
            return item.customImageUrl;
        }
        if (item.style?.imageUrl) {
            return item.style.imageUrl;
        }
        if (item.productMaster?.imageUrl) {
            return item.productMaster.imageUrl;
        }
        // Return null if no image
        return null;
    };

    const handleCancelOrder = () => {
        if (cancelComment.trim().length == 0) {
            toast.error("Please provide a reason for cancellation");
            return;
        }
        setIsLoading(true);
        ArthaGateway.cancelSalesOrder(order.id, cancelComment, 1).then(() => {
            toast.success("Order cancelled successfully");
            loadOrderDetails();
            setCancelComment("");
            setIsCancelling(false);
        }).catch(error => {
            toast.error("Error cancelling order");
            setIsLoading(false);
            console.log(error);
        });
    };

    const currencyFormat = (value) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(value);

    return (
        <div className='page page-order-details'>
            {isLoading &&
                <Loader />
            }
            {!isLoading &&
                <>
                    <div className='row'>
                        <div className='col-12 col-md-6 col-lg-4 offset-md-3 offset-lg-4 my-2'>
                            <div><Toaster /></div>
                            <div key={order.id} className="card mb-2">
                                <div className="card-header">
                                    <h3 className="order-id">Order ID: <b>{order.voucherNumber}</b></h3>
                                </div>
                                <div className="card-body pt-2 pb-0">
                                    <div className="order-date">Date:&nbsp; {moment(order.date).local().format('D/M/YYYY')}</div>
                                    <div className="order-status">
                                        Status:&nbsp;
                                        {OrderStatuses.filter(s => s.id === order.status).map((s) => (
                                            <span key={s.id} className={s.class}>{s.name}</span>
                                        ))}
                                    </div>
                                    {order.extraInformation && order.extraInformation.length > 0 && (
                                        <div>
                                            Brand:&nbsp;
                                            {order.extraInformation}
                                        </div>
                                    )}
                                    {(order.status == 1) && (
                                        <div className="alert alert-danger p-0 text-center" style={{ margin: 10 }}>
                                            Order is yet to be confirmed.
                                        </div>
                                    )}
                                </div>
                                <div className="card-body p-1">
                                    {order.items.map((item, index) => (
                                        < div className='p-0' key={index}>
                                            <div className="card-header item-header p-1">
                                                <b>{item.product.name}</b>
                                                {item.style?.name && (
                                                    <div className="item-style">Style: {item.style.name}</div>
                                                )}
                                                {item.notes && (
                                                    <div className="item-narration">Note: <span className="text-danger fw-bold">{item.notes}</span></div>
                                                )}
                                            </div>
                                            <div className="row align-items-start g-2">
                                                {/* Left: Product Image */}
                                                <div className="col-6">
                                                    <div className="card h-100">
                                                        <div className="card-img-top text-center">
                                                            {getDisplayImage(item)
                                                                ? <img src={getDisplayImage(item)} alt="product" style={{ maxHeight: '200px' }} />
                                                                : <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '200px', color: '#ccc' }}>
                                                                    <span>No Image</span>
                                                                </div>
                                                            }
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Right: Status Cards (Stacked Vertically) */}
                                                <div className="col-6">
                                                    <div className="d-flex flex-column gap-0">
                                                        {/* Confirmed Card */}
                                                        <div className="card text-center p-1">
                                                            <span className="stage-name">Confirmed</span>
                                                            <div className="d-inline-flex justify-content-center align-items-center fw-bold">
                                                                {(order.status > 1)
                                                                    ? <>
                                                                        <FontAwesomeIcon icon={faCircleCheck} className="text-success me-1" />
                                                                        {item.quantity}
                                                                    </>
                                                                    : <span className="text-muted">--</span>
                                                                }
                                                            </div>
                                                        </div>

                                                        {/* Pressed Card */}
                                                        <div className="card text-center p-1">
                                                            <span className="stage-name">Pressed</span>
                                                            <div className="d-inline-flex justify-content-center align-items-center fw-bold">
                                                                {(item.pressedQuantity && item.quantity <= item.pressedQuantity)
                                                                    ? <FontAwesomeIcon icon={faCircleCheck} className="text-success me-1" />
                                                                    : null
                                                                }
                                                                {item.pressedQuantity || <span className="text-muted">0</span>}
                                                            </div>
                                                        </div>

                                                        {/* Packed Card */}
                                                        <div className="card text-center p-1">
                                                            <span className="stage-name">Packed</span>
                                                            <div className="d-inline-flex justify-content-center align-items-center fw-bold">
                                                                {(item.packedQuantity && item.quantity <= item.packedQuantity)
                                                                    ? <FontAwesomeIcon icon={faCircleCheck} className="text-success me-1" />
                                                                    : null
                                                                }
                                                                {item.packedQuantity || <span className="text-muted">0</span>}
                                                            </div>
                                                        </div>

                                                        {/* Dispatched Card */}
                                                        <div className="card text-center p-1">
                                                            <span className="stage-name">Dispatched</span>
                                                            <div className="d-inline-flex justify-content-center align-items-center fw-bold">
                                                                {(item.pendingQuantity === 0)
                                                                    ? <FontAwesomeIcon icon={faCircleCheck} className="text-success me-1" />
                                                                    : null
                                                                }
                                                                {(item.quantity - item.pendingQuantity) || <span className="text-muted">0</span>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    {(order.status == 1) && (
                        <>
                            {isCancelling && (
                                <div className="row mb-2">
                                    <div className="col-12 col-md-6 col-lg-4 offset-md-3 offset-lg-4">
                                        <textarea className="form-control" rows={3} placeholder="Reason for cancellation" value={cancelComment} onChange={(e) => setCancelComment(e.target.value)} />
                                        <button type="button" className="btn btn-danger btn-lg w-100 mt-2" onClick={handleCancelOrder}>Confirm Cancellation</button>
                                    </div>
                                </div>
                            )}
                            {!isCancelling && (
                                <div className="row mb-2">
                                    <div className="col-12 col-md-6 col-lg-4 offset-md-3 offset-lg-4">
                                        <button type="button" className="btn btn-danger btn-lg w-100" onClick={() => { setIsCancelling(true) }}>Cancel Order</button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    <div className="row mb-2">
                        <div className="col-12 col-md-6 col-lg-4 offset-md-3 offset-lg-4">
                            <button type="button" className="btn btn-danger btn-sm w-100" onClick={() => { navigate(-1) }}>Back</button>
                        </div>
                    </div>
                </>
            }
        </div>
    );
};